import { useRef } from 'react'
import { motion } from 'framer-motion'
import { useInView } from '../../hooks/useInView'
import ctaIcon from '../../assets/cta-02.svg'

const tiers = [
  {
    name: 'Starter',
    description: 'Everything you need to launch your first community.',
    cta: 'Get Started Free',
    ctaStyle: 'bg-white border border-oak-black/15 text-oak-black hover:shadow-lg',
    ctaHref: '#start',
    highlight: false,
    features: [
      { label: 'White-label mobile apps', included: true },
      { label: 'Stripe payments', included: true },
      { label: 'Shippo shipping integration', included: true },
      { label: 'Community marketplace', included: true },
      { label: 'Member management', included: true },
      { label: 'Branded transactional emails', included: true },
      { label: 'Affiliate tools', included: false },
      { label: 'Cortex Basic', included: true },
      { label: 'Agentic commerce', included: false },
      { label: 'SDK access', included: false },
      { label: 'Custom APIs', included: false },
      { label: 'Dedicated support', included: false },
    ],
  },
  {
    name: 'Growth',
    description: 'For growing communities that want more ways to reach people and reward them.',
    cta: 'Request a Demo',
    ctaStyle: 'bg-oak-cream text-oak-black hover:opacity-90',
    ctaHref: '#demo',
    highlight: true,
    features: [
      { label: 'White-label mobile apps', included: true },
      { label: 'Stripe payments', included: true },
      { label: 'Shippo shipping integration', included: true },
      { label: 'Community marketplace', included: true },
      { label: 'Member management', included: true },
      { label: 'Branded transactional emails', included: true },
      { label: 'Affiliate tools', included: true },
      { label: 'Cortex Pro', included: true },
      { label: 'Agentic commerce', included: true },
      { label: 'SDK access', included: true },
      { label: 'Custom APIs', included: false },
      { label: 'Dedicated support', included: false },
    ],
  },
  {
    name: 'Enterprise',
    description: 'For large communities and networks of communities that need custom work and a dedicated hand.',
    cta: 'Talk to us',
    ctaStyle: 'bg-white border border-oak-black/15 text-oak-black hover:shadow-lg',
    ctaHref: 'mailto:info@oakplatforms.com',
    highlight: false,
    features: [
      { label: 'White-label mobile apps', included: true },
      { label: 'Stripe payments', included: true },
      { label: 'Shippo shipping integration', included: true },
      { label: 'Community marketplace', included: true },
      { label: 'Member management', included: true },
      { label: 'Branded transactional emails', included: true },
      { label: 'Affiliate tools', included: true },
      { label: 'Cortex Unlimited', included: true },
      { label: 'Agentic commerce', included: true },
      { label: 'SDK access', included: true },
      { label: 'Custom APIs', included: true },
      { label: 'Dedicated support', included: true },
    ],
  },
]

function CheckIcon({ active, onDark }: { active: boolean; onDark?: boolean }) {
  const color = active
    ? 'text-oak-green'
    : onDark
      ? 'text-white/30'
      : 'text-oak-black/20'
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={`shrink-0 ${color}`}
    >
      {active ? (
        <path
          d="M16.667 5L7.5 14.167 3.333 10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M5 10h10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
    </svg>
  )
}

function TierCard({
  tier,
  index,
}: {
  tier: (typeof tiers)[number]
  index: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { threshold: 0.2 })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.7,
        delay: index * 0.12,
        ease: [0.38, 0.005, 0.215, 1] as const,
      }}
      className={`flex flex-col rounded-3xl p-8 md:p-10 ${
        tier.highlight
          ? 'bg-oak-black text-white ring-2 ring-oak-yellow'
          : 'bg-white'
      }`}
    >
      <div>
        <h3
          className={`text-[clamp(24px,2.5vw,32px)] font-semibold tracking-[-0.5px] ${
            tier.highlight ? 'text-white' : 'text-oak-black'
          }`}
        >
          {tier.name}
        </h3>
        <p
          className={`mt-2 text-[15px] leading-[1.5] tracking-[-0.2px] ${
            tier.highlight ? 'text-white/60' : 'text-oak-black/60'
          }`}
        >
          {tier.description}
        </p>
      </div>

      <div className="mt-8 flex-1">
        <ul className="space-y-3.5">
          {tier.features.map((f) => (
            <li key={f.label} className="flex items-center gap-3">
              <CheckIcon active={f.included} onDark={tier.highlight} />
              <span
                className={`text-[15px] tracking-[-0.2px] ${
                  f.included
                    ? tier.highlight
                      ? 'text-white'
                      : 'text-oak-black'
                    : tier.highlight
                      ? 'text-white/30'
                      : 'text-oak-black/30'
                }`}
              >
                {f.label}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-10">
        <a
          href={tier.ctaHref}
          className={`flex w-full items-center justify-center gap-3 rounded-pill px-6 py-4 text-[16px] font-medium tracking-[-0.4px] transition-all ${tier.ctaStyle}`}
        >
          {tier.cta}
          {tier.highlight ? (
            <img src={ctaIcon} alt="" className="h-7 w-7" />
          ) : (
            <span className="h-7 w-7 rounded-full bg-oak-gray" />
          )}
        </a>
      </div>
    </motion.div>
  )
}

export default function PricingTable() {
  const headingRef = useRef<HTMLDivElement>(null)
  const headingInView = useInView(headingRef, { threshold: 0.3 })

  return (
    <section className="rounded-section bg-white px-6 py-16 md:px-16 md:py-24">
      <div className="mx-auto max-w-6xl">
        <motion.div
          ref={headingRef}
          initial={{ opacity: 0, y: 40 }}
          animate={headingInView ? { opacity: 1, y: 0 } : {}}
          transition={{
            duration: 0.8,
            ease: [0.38, 0.005, 0.215, 1] as const,
          }}
          className="mb-12 text-center md:mb-16"
        >
          <h2 className="font-serif text-[14vw] leading-[0.95] tracking-[-1.5px] md:text-[clamp(36px,5vw,64px)] text-oak-black">
            What&apos;s included
          </h2>
          <p className="mx-auto mt-4 max-w-[480px] text-[clamp(16px,1.6vw,20px)] font-medium leading-[1.4] tracking-[-0.2px] text-oak-black/60">
            Every plan runs on the same platform. Pick the one that fits where your community is today.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {tiers.map((tier, i) => (
            <TierCard key={tier.name} tier={tier} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
